import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { db } from 'src/app/db/db';
import { Cassette } from 'src/app/domain/cassette';
import { EAPRType } from 'src/app/domain/enums/APR-type';
import { EEntreeProcessType } from 'src/app/domain/enums/entree-process-type';
import { EEPIType } from 'src/app/domain/enums/epi-type';
import { EExpositionClimatique } from 'src/app/domain/enums/exposition-climatique';
import { EIntegriteType } from 'src/app/domain/enums/integrite-type';
import { EMeteoType } from 'src/app/domain/enums/meteo-type';
import { EMoyenObservation } from 'src/app/domain/enums/moyen-observation';
import { EPostureType } from 'src/app/domain/enums/posture-type';
import { ESamplingMode } from 'src/app/domain/enums/sampling-mode';
import { ETetePrelevement } from 'src/app/domain/enums/tete-prelevement';
import { Operator } from 'src/app/domain/operator';
import { PlageHoraire } from 'src/app/domain/plage-horaire';
import { Pompe } from 'src/app/domain/pompe';
import { ProcessMeasure } from 'src/app/domain/process-mesure';
import { Task } from 'src/app/domain/task';
import { ENavigationRoute } from 'src/app/model/enum/navigation-route';
import { ProcessService } from 'src/app/services/process.service';
import { CoreData } from 'src/app/tools/core-data/core-data';
import { StringTool } from 'src/app/tools/mapping/string.tool';
import { TaskBoxDialog } from 'src/app/tools/ui/add-task-box/add-task-box';
import { ColorService } from 'src/app/tools/ui/color.service';
import { ConfirmBoxDialog } from 'src/app/tools/ui/confirm-box/confirm-box.component';
import { EditTextBoxDialog } from 'src/app/tools/ui/edit-text-box/edit-text-box';
import { InitOperatorBoxDialog } from 'src/app/tools/ui/init-operator-box/init-operator-box';
import { LoadingTask } from 'src/app/tools/ui/loading-task';
import { NotificationManager } from 'src/app/tools/ui/notification-manager';
import { PlageHoraireBoxDialog } from 'src/app/tools/ui/plage-horaire-box/plage-horaire-box';
import { StringConverter } from 'src/app/tools/ui/string-converter';

@Component({
    selector: 'app-process',
    templateUrl: './process.component.html',
    styleUrls: ['./process.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProcessComponent implements OnInit {

    EExpositionClimatique = EExpositionClimatique

    public TetePrelevementList: string[] = Object.values(ETetePrelevement)
        .filter(x => !isNaN(Number(x)))
        .map((x: any) => StringConverter.getTetePrelevementTypeName(x));

    public MeteoList: string[] = Object.values(EMeteoType)
        .filter(x => !isNaN(Number(x)))
        .map((x: any) => StringConverter.getMeteoTypeName(x));

    // public EntreProcessList: string[] = Object.values(EEntreeProcessType)
    //     .filter(x => !isNaN(Number(x)))
    //     .map((x: any) => StringConverter.getEntreProcessTypeName(x));

    public MoyenObservationList: string[] = Object.values(EMoyenObservation)
        .filter(x => !isNaN(Number(x)))
        .map((x: any) => StringConverter.getMoyenObservationName(x));

    public EPIList: string[] = Object.values(EEPIType)
        .filter(x => !isNaN(Number(x)))
        .map((x: any) => StringConverter.getEPITypeName(x));

    public APRList: string[] = Object.values(EAPRType)
        .filter(x => !isNaN(Number(x)))
        .map((x: any) => StringConverter.getAPRTypeName(x));

    public IntegriteList: string[] = Object.values(EIntegriteType)
        .filter(x => !isNaN(Number(x)))
        .map((x: any) => StringConverter.getIntegriteTypeName(x));

    public ExpositionClimatiqueList: string[] = Object.values(EExpositionClimatique)
        .filter(x => !isNaN(Number(x)))
        .map((x: any) => StringConverter.getExpositionClimatiqueTypeName(x));

    public SamplingModeList: string[] = Object.values(ESamplingMode)
        .filter(x => !isNaN(Number(x)))
        .map((x: any) => StringConverter.getSamplingModeName(x));

    CompteurAvecHeure: boolean = false;
    // CompteurHoraireInitialHeures: number | undefined;
    // CompteurHoraireInitialMinutes: number | undefined;
    // CompteurHoraireFinalHeures: number | undefined;
    // CompteurHoraireFinalMinutes: number | undefined;

    ValidMinutesForHoraireInitial = new UntypedFormControl('', Validators.compose([Validators.min(0), Validators.max(59)]));
    ValidMinutesForHoraireFinal = new UntypedFormControl('', Validators.compose([Validators.min(0), Validators.max(59)]));

    Measure!: ProcessMeasure;
    PumpCassetteOptions = new Map<string, Cassette[]>();
    SelectedPumpCassettes = new Map<Cassette, Cassette>();
    private mrCode: string | undefined;
    private processCode: string | undefined;
    public SamplingMode: ESamplingMode | undefined;
    private scannedCode?: string;

    constructor(
        public LoadingTask: LoadingTask,
        private coreData: CoreData,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private route: ActivatedRoute,
        private processService: ProcessService,
        private dialog: MatDialog) { }

    ngOnInit(): void {
        this.mrCode = this.route.snapshot.params?.['measureCode'];
        this.processCode = this.route.snapshot.params?.['processCode'];
        this.SamplingMode = Number(this.route.snapshot.params?.['samplingMode']);

        this.ValidMinutesForHoraireInitial.markAsTouched();
        this.ValidMinutesForHoraireFinal.markAsTouched();
    }

    async ngAfterContentInit() {
        await this.init();
    }

    async init() {
        this.Measure = await db.getProcessMeasureByCodeWithChildren(this.mrCode!);

        if (this.Measure.ProcessOperatorList.some(x => !x.Position)) {
            const response: Operator[] = await lastValueFrom(this.openOperatorConfigurationDialog())
            if (response) {
                this.Measure.ProcessOperatorList = response;
                await this.save(true);
                this.Measure = await db.getProcessMeasureByCodeWithChildren(this.mrCode!); // Get the Ids from the DB
                NotificationManager.handleSuccess('Opérateurs configurés', this.snackBar);
            } else {
                this.location.back()
            }
        }

        this.tryToGetScannedCode();
    }

    checkBarCode(barCode: string) {
        if (!barCode || barCode === '')
            return true;
        var regex = /^([A-Za-z0-9]{5,7}[:-][A-Za-z0-9]{5,8}|[A-Za-z0-9]{5,6}-[A-Za-z0-9]{5}|[A-Za-z0-9]{6,8}[ ]{0,2}-[A-Za-z0-9]{5}|[A-Za-z0-9]{7}-[A-Za-z0-9]{4}-[A-Za-z0-9]{6})$/g
        let success = regex.test(barCode)

        return success
    }
    
    async tryToGetScannedCode() {
        if (this.coreData.ScannedCassetteCode && this.coreData.ScannedId) {
            const cassette = this.Measure.ProcessOperatorList.find(x => x.CassetteList.find(x => x.Id === Number(this.coreData.ScannedId)))?.CassetteList.find(y => y.Id === Number(this.coreData.ScannedId));
            if (cassette) {
                cassette.Reference = this.coreData.ScannedCassetteCode;
            }
            this.coreData.ScannedId = '';
            this.coreData.ScannedCassetteCode = '';
            await this.save(true);
        }
        if (this.coreData.ScannedPompeCode && this.coreData.ScannedId) {
            const cassette = this.Measure.ProcessOperatorList.find(x => x.CassetteList.find(x => x.Id === Number(this.coreData.ScannedId)))?.CassetteList.find(y => y.Id === Number(this.coreData.ScannedId));
            if (cassette) {
                cassette.Pompe.Reference = this.coreData.ScannedPompeCode;
            }
            this.coreData.ScannedId = '';
            this.coreData.ScannedPompeCode = '';
            await this.save(true);
        }
        // TEMOIN
        if (this.coreData.ScannedCassetteCode && !this.coreData.ScannedId) {
            this.Measure.ProcessSamplingCondition.Temoin = this.coreData.ScannedCassetteCode;
            this.coreData.ScannedCassetteCode = '';
            await this.save(true);
        }
    }

    getSamplingModelLabel() {
        return StringConverter.getSamplingModeName(this.SamplingMode!);
    }

    computeDebitMoy(pompe: Pompe) {
        return (pompe.DebitInitial + pompe.DebitFinal) / 2;
    }

    computeVolumeTotal(pompe: Pompe) {
        return Number.isNaN(this.computeDebitMoy(pompe) * this.computeDureeTotal(pompe)) ? 0 : this.computeDebitMoy(pompe) * this.computeDureeTotal(pompe);
    }

    computeDureeTotal(pompe: Pompe) {
        return Number.isNaN((pompe.CompteurHoraireFinal - pompe.CompteurHoraireInitial)) ? 0 : (pompe.CompteurHoraireFinal - pompe.CompteurHoraireInitial);
    }

    computeDebitMoyInf5Percent(pompe: Pompe) {
        return Math.abs(pompe.DebitFinal - pompe.DebitInitial) * 100 / pompe.DebitInitial < 5 ? 'OUI' : 'NON'
    }

    getPostureLabel(posture: EPostureType) {
        return StringConverter.getPostureTypeName(posture);
    }

    deleteTask(operator: Operator, task: Task) {
        _.remove(this.Measure.ProcessOperatorList.find(x => x.Position === operator.Position)?.TaskList!, task);
    }

    canCreateTask(operator: Operator) {
        return true
    }

    openOperatorConfigurationDialog() {

        const dialogRef = this.dialog.open(InitOperatorBoxDialog, {
            data: {
                opCount: this.Measure.OperatorsCount,
                cassetteCount: this.Measure.CassetteCount,
                samplingMode: this.SamplingMode
            },
            width: '777px',
            disableClose: true
        });

        return dialogRef.afterClosed();
    }

    openTaskDialog(operator: Operator, task?: Task) {
        const tl = this.Measure.ProcessOperatorList.find(x => x.Position === operator.Position)?.TaskList!;
        const dialogRef = this.dialog.open(TaskBoxDialog, {
            data: {
                processCode: this.processCode,
                // startingDate: tl?.length === 0 ?
                //     operator.CassetteList[0]?.PlageHoraireList[0]?.Debut ? operator.CassetteList[0].PlageHoraireList[0].Debut : Temporal.Now.plainDateTimeISO() :
                //     tl[tl?.length! - 1].EndedOn
            },
            width: '777px',
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                result.Color = ColorService.getRandomPastelColor();
                this.Measure.ProcessOperatorList.find(x => x.Position === operator.Position)?.TaskList.push(result);
                NotificationManager.handleSuccess('Tache ajoutée', this.snackBar);
            }
        });
    }

    deletePlage(ph: PlageHoraire, cassette: Cassette, op: Operator) {
        const removed = _.remove(this.Measure.ProcessOperatorList?.find(x => x.Position === op.Position)?.CassetteList.find(k7 => k7.Reference === cassette.Reference)?.PlageHoraireList!, ph);
        if (removed)
            NotificationManager.handleSuccess('Plage horaire suprimée', this.snackBar);
    }

    openPlageHoraireDialog(operator: Operator, cassetteId: number) {

        const dialogRef = this.dialog.open(PlageHoraireBoxDialog, {
            data: {},
            width: '777px',
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            result.Color = ColorService.getRandomPastelColor();
            if (result) {
                if (this.SamplingMode === ESamplingMode.Simultaneous) {
                    this.Measure.ProcessOperatorList.forEach(op =>
                        op?.CassetteList.forEach(y => {
                            y.PlageHoraireList.push(result)
                        }));
                } else {
                    this.Measure.ProcessOperatorList.find(
                        x => x.Position === operator.Position)
                        ?.CassetteList.find(y => y.Id === cassetteId)
                        ?.PlageHoraireList.push(result);
                }
                NotificationManager.handleSuccess('Plage horaire ajoutée', this.snackBar);
            }
        });
    }

    /**
     * 
     * @param scannedId CassetteId or PompeId or Empty for Temoin
     * @param isForPompe 
     */
    async openBarcodeScanning(scannedId?: number, isForPompe?: boolean) {
        if (scannedId) {
            this.coreData.ScannedId = `${scannedId}`;
        }
        await this.save(true);
        this.router.navigate([ENavigationRoute.Scan, { isForPompe: isForPompe }]);
    }

    getTotal(cassette: Cassette) {
        if (cassette.PlageHoraireList.length === 0)
            return;
        const days = _.sum(cassette.PlageHoraireList.map(x => x.Fin.since(x.Debut).days)) * 60 * 24;
        const hours = _.sum(cassette.PlageHoraireList.map(x => x.Fin.since(x.Debut).hours)) * 60;
        const mins = _.sum(cassette.PlageHoraireList.map(x => x.Fin.since(x.Debut).minutes));
        return days + hours + mins;
    }

    cancelMeasure() {
        const dialogRef = this.dialog.open(EditTextBoxDialog, { data: { title: 'Veuillez indiquer le motif de l\'annulation', withCancelButton: true } });
        dialogRef.afterClosed().subscribe(async (result: string) => {
            if (result) {
                await db.cancelledProcessMeasure(this.Measure, result);
                NotificationManager.handleSuccess('Mesure annulée', this.snackBar);
                this.location.back()
            }
        })
    }

    deleteCassette(op: Operator, k7: Cassette) {
        const dialogRef = this.dialog.open(ConfirmBoxDialog, { data: { message: 'Etes-vous sûr de vouloir annuler cette cassette ?', withCancelButton: true } });
        dialogRef.afterClosed().subscribe(async (result: boolean) => {
            if (result) {
                _.remove(op.CassetteList, x => x.Id === k7.Id)
                await this.save(true);
                NotificationManager.handleSuccess('Cassette annulée', this.snackBar);
            }
        })
    }
    async deleteOperator(op: Operator) {
        const dialogRef = this.dialog.open(ConfirmBoxDialog, { data: { message: 'Etes-vous sûr de vouloir annuler cet opérateur ?', withCancelButton: true } });
        dialogRef.afterClosed().subscribe(async (result: boolean) => {
            if (result) {
                await db.deleteOperator(this.Measure, op)
                _.remove(this.Measure.ProcessOperatorList, x => x.FirstName === op.FirstName && x.LastName === op.LastName);
                NotificationManager.handleSuccess('Opérateur annulée', this.snackBar);
            }
        })
    }

    getColor() {
        return ColorService.getRandomPastelColor();
    }

    async changeSamplingMode(event: MatSelectChange) {

        const success = await db.updateSamplingMode(this.processCode!, event.value)
        if (success) {
            NotificationManager.handleSuccess('Mode de prélèvement modifié', this.snackBar);
        }
    }

    async changeExposition(event: MatSelectChange) {

        if (event.value === 1) {
            this.Measure.ProcessSamplingCondition.Meteo = undefined;
            this.Measure.ProcessSamplingCondition.VitesseVent1 = undefined;
            this.Measure.ProcessSamplingCondition.VitesseVent2 = undefined;
            this.Measure.ProcessSamplingCondition.VitesseVent3 = undefined;
            this.Measure.ProcessSamplingCondition.Hygrometrie1 = undefined;
            this.Measure.ProcessSamplingCondition.Hygrometrie2 = undefined;
            this.Measure.ProcessSamplingCondition.Hygrometrie3 = undefined;
        }

    }

    canSave() {
        for (let i = 0; i < this.Measure.ProcessOperatorList.length; i++) {
            for (let j = 0; j < this.Measure.ProcessOperatorList[i].CassetteList.length; j++ ) {
                if (!StringTool.isNotNullOrEmpty(this.Measure.ProcessOperatorList[i].CassetteList[j].Reference) || !this.checkBarCode(this.Measure.ProcessOperatorList[i].CassetteList[j].Reference))
                    return false;
            }
        }
        return true;
    }

    updateEntreeProcessType() {
        const exposition = this.Measure.ProcessSamplingCondition.ExpositionClimatique;
        if (exposition !== EExpositionClimatique.Interieur &&
            exposition !== EExpositionClimatique.Exterieur &&
            exposition !== EExpositionClimatique.SemiOuvert) {
            return;
        }

        switch (this.Measure.Securite.MoyenObservation) {
            case EMoyenObservation.EnZoneDeTravail:
                this.Measure.Securite.EntreeZone = exposition === EExpositionClimatique.Interieur
                    ? EEntreeProcessType.IntZoneTraitement
                    : EEntreeProcessType.ExtZoneTraitement;
                break;
            case EMoyenObservation.BulleDeVision:
                this.Measure.Securite.EntreeZone = EEntreeProcessType.SAS;
                break;
            case EMoyenObservation.Camera:
                this.Measure.Securite.EntreeZone = exposition === EExpositionClimatique.Interieur
                    ? EEntreeProcessType.SAS
                    : EEntreeProcessType.ExtPerimetreChantier;
                break;
            case EMoyenObservation.ExterieurDansLePerimetreDeChantier:
                this.Measure.Securite.EntreeZone = EEntreeProcessType.ExtPerimetreChantier;
                break;
        }
    }
    
    canSynchronizePumps(): boolean {
        return !!this.Measure?.ProcessOperatorList.every(operator =>
            operator.CassetteList.every(cassette => !!cassette.Pompe?.Reference?.trim()));
    }

    getPumpCassetteOptions(cassette: Cassette): Cassette[] {
        return this.PumpCassetteOptions.get(cassette.Pompe?.Reference) ?? [];
    }

    getPumpCassetteLabel(cassette: Cassette): string {
        const periods = cassette.PlageHoraireList.map(period => {
            const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'numeric', hour: 'numeric', minute: 'numeric' };
            return `${period.Debut.toLocaleString('fr-FR', options)} au ${period.Fin.toLocaleString('fr-FR', options)}`;
        });
        return `${cassette.Reference || 'Sans reference'} : ${periods.join(' ; ') || 'Sans plage horaire'}`;
    }

    selectPumpCassette(cassette: Cassette, serverCassette: Cassette) {
        if (!this.getPumpCassetteOptions(cassette).includes(serverCassette)) {
            return;
        }

        this.SelectedPumpCassettes.set(cassette, serverCassette);
        cassette.PlageHoraireList = serverCassette.PlageHoraireList;
        const pump = cassette.Pompe;
        const serverPump = serverCassette.Pompe;
        pump.DebitInitial = serverPump.DebitInitial;
        pump.DebitFinal = serverPump.DebitFinal;
        pump.CompteurHoraireInitial = serverPump.CompteurHoraireInitial;
        pump.CompteurHoraireFinal = serverPump.CompteurHoraireFinal;
        pump.CompteurVolInitial = serverPump.CompteurVolInitial;
        pump.CompteurVolFinal = serverPump.CompteurVolFinal;
        pump.CompteurHoraireInitialHeures = serverPump.CompteurHoraireInitial === undefined
            ? undefined : Math.floor(serverPump.CompteurHoraireInitial / 60);
        pump.CompteurHoraireInitialMinutes = serverPump.CompteurHoraireInitial === undefined
            ? undefined : serverPump.CompteurHoraireInitial % 60;
        pump.CompteurHoraireFinalHeures = serverPump.CompteurHoraireFinal === undefined
            ? undefined : Math.floor(serverPump.CompteurHoraireFinal / 60);
        pump.CompteurHoraireFinalMinutes = serverPump.CompteurHoraireFinal === undefined
            ? undefined : serverPump.CompteurHoraireFinal % 60;
    }

    synchronizePumps() {
        if (!this.canSynchronizePumps()) {
            NotificationManager.handleError('Vous devez remplir toutes les références des pompes avant de synchroniser.', this.snackBar);
            return;
        }

        this.processService.getAutomatiquePompeOPData(this.Measure.ProcessOperatorList.map(operator => operator.CassetteList).flat())
        .subscribe({
            next: (data) => {
                const cassettesByReference = new Map(data.map(cassette => [cassette.Reference, cassette]));
                this.PumpCassetteOptions.clear();
                this.SelectedPumpCassettes.clear();
                for (const cassette of data) {
                    const reference = cassette.Pompe?.Reference;
                    if (reference) {
                        const candidates = this.PumpCassetteOptions.get(reference) ?? [];
                        candidates.push(cassette);
                        this.PumpCassetteOptions.set(reference, candidates);
                    }
                }

                for (const operator of this.Measure.ProcessOperatorList) {
                    for (const cassette of operator.CassetteList) {
                        const candidates = this.getPumpCassetteOptions(cassette);
                        if (candidates.length === 1) {
                            this.selectPumpCassette(cassette, candidates[0]);
                        } else if (candidates.length === 0) {
                            const serverCassette = cassettesByReference.get(cassette.Reference);
                            if (serverCassette) {
                                cassette.PlageHoraireList = serverCassette.PlageHoraireList;
                            }
                        }
                    }
                }
                if (this.Measure.ProcessOperatorList.some(operator =>
                    operator.CassetteList.some(cassette => this.getPumpCassetteOptions(cassette).length > 1))) {
                    NotificationManager.handleWarning(
                        'Des pompes apparaissent plusieurs fois dans les données du serveur. Veuillez choisir la bonne cassette pour chaque pompe concernée à l’aide des plages horaires affichées.',
                        this.snackBar);
                }
            },
            error: (err) => {
                console.error('Error synchronizing pumps:', err);
            }
        });
    }

    async save(quiet?: boolean) {
        this.updateEntreeProcessType();
        this.LoadingTask.activateLoading();
        await db.clearProcessMeasureChildren(this.Measure);
        const success = await db.attachProcessMeasureNewChildByCode(this.Measure);
        if (success && !quiet) {
            NotificationManager.handleSuccess('Mesure enregistrée', this.snackBar);
            this.location.back()
        } else if (!success) {
            NotificationManager.handleError('Wasted Bro!', this.snackBar);
        }
        this.LoadingTask.deactivateLoading();
    }
}
